import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  UntypedFormGroup,
  Validators,
  UntypedFormControl,
  AsyncValidatorFn,
  ValidatorFn,
} from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { timer, Observable, Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IDeliveryTarget } from 'src/app/core/models/i-delivery-target';
import { TitleCasePipe, LowerCasePipe } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { AlertService } from 'src/app/shared/services/alert.service';
import {
  debounceTime,
  map,
  distinctUntilChanged,
  switchMap,
  take,
} from 'rxjs/operators';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { WebsitePrefixMappings } from '../shared/mappings/website-prefix-mappings';
import { DeliveryTargetsService } from '../shared/services/cmsapi/delivery-targets.service';
import { ContentListService } from '../shared/services/cmsapi/content-list.service';
import { HttpResponse, HttpStatusCode } from '@angular/common/http';
import { CitywireInternalAccountsService } from '@tpapa/citywire-internal-accounts-angular';

@Component({
  selector: 'app-create-content-list',
  templateUrl: './create-content-list.component.html',
})
export class CreateContentListComponent implements OnInit, OnDestroy {
  contentListForm: UntypedFormGroup;
  submitted = false;
  currentUserName = '';
  contentListCode: string;
  websiteList: IDeliveryTarget[] = [];
  contentListTypeList: string[] = [];
  createTimerDuration = 2000;
  createTimer: Observable<number> = timer(this.createTimerDuration);
  subscriptions: Subscription[] = [];
  debounceTime: number = 500;
  websitePrefixMappings = WebsitePrefixMappings;

  constructor(
    private deliveryTargetsService: DeliveryTargetsService,
    private contentListService: ContentListService,
    private accountsService: CitywireInternalAccountsService,
    private titlecasePipe: TitleCasePipe,
    private lowerCasePipe: LowerCasePipe,
    private router: Router,
    private alertService: AlertService,
    private route: ActivatedRoute,
    private dialogService: DialogService
  ) {}

  ngOnInit() {
    this.contentListTypeList = this.contentListService.getTypes$();

    this.contentListForm = new UntypedFormGroup({
      website: new UntypedFormControl(
        this.route.snapshot.queryParams['website'],
        [Validators.required]
      ),
      contentListType: new UntypedFormControl(this.contentListTypeList[0], [
        Validators.required,
      ]),
      name: new UntypedFormControl(
        '',
        [Validators.required, this.checkLength()],
        this.checkIfNameExists()
      ),
      description: new UntypedFormControl(''),
    });

    const deliveryTargetSubscription = this.deliveryTargetsService
      .getAll$()
      .subscribe(
        (data) => {
          this.websiteList = data.filter((website) => website.isTierOneSite);
          this.formatContentListCode(this.contentListForm.value);
        },
        (error) => {
          this.dialogService.openErrorDialog({
            message:
              'An error has occurred whilst loading the websites list. Please contact IT.',
          });
        }
      );

    const contentListFormSubscription =
      this.contentListForm.valueChanges.subscribe((contentList) => {
        this.formatContentListCode(contentList);
      });

    const currentUserNameSubscription = this.accountsService.currentUser
      .pipe(take(1))
      .subscribe(
        (user) => (this.currentUserName = user.username.toLowerCase())
      );

    this.subscriptions.push(
      deliveryTargetSubscription,
      contentListFormSubscription,
      currentUserNameSubscription
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  resetInputNameValidation() {
    this.contentListForm.controls['name'].updateValueAndValidity({
      onlySelf: true,
      emitEvent: true,
    });
  }

  checkIfNameExists(): AsyncValidatorFn {
    return (control) =>
      control.valueChanges.pipe(
        debounceTime(this.debounceTime),
        distinctUntilChanged(),
        take(1),
        switchMap((value) => {
          // Certain characters cause IIS to throw errors complaining about unsafe URLs, and all punctuation is stripped on ContentListManagementApi side anyway
          var name = value.replace(/[\?\+\#\%\\\/]/g, '');
          // Even using encodeURI() here IIS interprets the ampersand as a separate parameter, so need to replace with and (also done on API end)
          name = name.replace(/\&/g, ' and ');

          if (!name) {
            return of(true);
          }
          return this.contentListService.contentListExistsByName$(
            this.formatWebsitePrefix(this.contentListForm.value.website) + name
          );
        }),
        catchError((x) => {
          this.dialogService.openErrorDialog({
            message:
              'An error has occurred whilst checking the content list name. Please contact IT.',
          });
          return of(null);
        }),
        map((event) => {
          if (event instanceof HttpResponse && event.status === 200) {
            return { contentListNameExists: true };
          }
          if (event instanceof HttpResponse && event.status === 204) {
            return null;
          }
          return { contentListNameFailure: true };
        })
      );
  }

  checkLength(): ValidatorFn {
    return (control) => {
      if (control && control.value) {
        var minNameLength = 3;
        var maxNameLength = 50;
        var nameWithPrefix =
          this.formatWebsitePrefix(this.contentListForm.value.website) +
          control.value.trim();
        var valid =
          nameWithPrefix.length >= minNameLength &&
          nameWithPrefix.length <= maxNameLength;
        return valid ? null : { contentListNameLengthInvalid: true };
      }
    };
  }

  createContentList() {
    this.contentListForm.markAllAsTouched();
    if (this.contentListForm.invalid) {
      return;
    }

    this.submitted = true;

    this.contentListForm.value.name =
      this.formatWebsitePrefix(this.contentListForm.value.website) +
      this.contentListForm.value.name.trim().replace(/&/g, ' and ');
    const request = this.contentListForm.value;

    const createContentListSubscription = this.contentListService
      .create$(request)
      .subscribe(
        (result) => {
          if (result) {
            const createTimerSubscription = this.createTimer.subscribe((x) => {
              this.contentListForm.reset();
              this.alertService.success(
                'List: ' + request.name + ' created successfully'
              );
              this.router.navigate(['./'], {
                relativeTo: this.route.parent,
                queryParams: { website: request.website },
              });
            });
            this.subscriptions.push(createTimerSubscription);
          } else {
            this.submitted = false;
            this.alertService.error(
              'Content list creation failed. Please try again.'
            );
          }
        },
        (error) => {
          this.contentListForm.reset();
          this.contentListForm.value.name = '';
          this.submitted = false;
          this.dialogService.openErrorDialog({
            message:
              'An error has occurred whilst creating the content list. Please contact IT.',
          });
        }
      );
    this.subscriptions.push(createContentListSubscription);
  }

  formatWebsitePrefix(websiteName: string): string {
    const website = this.websiteList.find(
      (website) => website.friendlyName === websiteName
    );
    if (website && website.code) {
      var prefix = this.getMappedPrefixIfExists(website.code);

      return prefix
        ? prefix + ': '
        : this.titlecasePipe.transform(
            website.code.substring(16).split('_').join(' ') + ': '
          );
    }
    return '';
  }

  formatContentListCode(contentList) {
    const website = this.websiteList.find(
      (website) => website.friendlyName === contentList.website
    );
    if (website && website.code) {
      this.contentListCode = this.lowerCasePipe.transform(
        website.code.substring(16).split('_').join('-') +
          '-' +
          this.lowerCasePipe.transform(contentList.name.split(' ').join('-'))
      );
    }
  }

  private getMappedPrefixIfExists(websiteCode: string): string {
    var mapping = this.websitePrefixMappings.find(
      (mapping) =>
        mapping.websiteCode.toLowerCase() === websiteCode.toLowerCase()
    );
    var prefix: string = null;
    if (mapping && mapping.prefix) {
      prefix = mapping.prefix;
    }

    return prefix;
  }
}
